import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { VueQueryPlugin } from '@tanstack/vue-query';

import App from './App.vue';
import { router } from './router';
import { log } from './lib/log';
import { installVueErrorHandler } from './lib/vueErrorHandler';
import { registerServiceWorker } from './lib/serviceWorker';
import { notificationClickTarget } from './lib/epgNotificationPlan';

import './styles/tokens.css';
import './styles/base.css';
import './styles/tile-context-menu.css';

const app = createApp(App);

installVueErrorHandler(app);

app.use(createPinia());
app.use(router);
app.use(VueQueryPlugin, {
    queryClientConfig: {
        defaultOptions: {
            queries: {
                staleTime: 30_000,
                gcTime: 5 * 60_000,
                refetchOnWindowFocus: false,
                retry: 1,
            },
        },
    },
});

app.mount('#app');

log.info('app.boot', {
    base: import.meta.env.BASE_URL,
    mode: import.meta.env.MODE,
});

// Service worker — precache + offline shell. Registration only; updates
// land on their own because navigations are network-first (see
// lib/serviceWorker.ts for the measurement behind that).
registerServiceWorker();

// EPG notification click → the channel the notification was about. The SW
// (web/src/sw.ts) focuses this tab and posts `epg-notification-click` with
// the notification's `data`; `notificationClickTarget` turns that into an
// app path (`/live?source=..&channel=..`, which `Live.vue`'s
// `consumeDeepLink()` tunes) and refuses anything that isn't app-relative.
//
// Only notifications created via `registration.showNotification()` reach the
// SW's `notificationclick` at all — a `new Notification()` from a document
// dispatches its click to that document instead, which is why
// `useEpgNotifications` prefers the SW path and carries its own in-page
// fallback (wired in App.vue) for when no SW is active.
//
// Improves on the legacy port (legacy-public/js/app.js:88-93), which pushed
// a bare '/#live' because the legacy notification carried no channel id.
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener?.('message', (event) => {
        const message = event.data as { type?: string; data?: unknown } | null;
        if (message?.type !== 'epg-notification-click') return;
        const target = notificationClickTarget(message.data);
        log.info('sw.notification_click_received', { path: target.split('?')[0] });
        router.push(target).catch(() => undefined);
    });
}
